import {
  Component,
  computed,
  contentChild,
  input,
  signal,
} from '@angular/core';
import {
  AvatarOnlineStatus,
  AvatarSize,
  ImageFetchPriority,
  ImageLoading,
} from './avatar.types';
import { NttAvatarContent } from './components/content';
import { NttAvatarCompanyIcon } from './components/company-icon';
import { NttAvatarOnlineIndicator } from './components/online-indicator';
import { NttAvatarVerifiedIcon } from './components/verified-icon';

@Component({
  selector: 'ntt-avatar',
  templateUrl: './avatar.html',
  styleUrl: './avatar.scss',
  imports: [NttAvatarOnlineIndicator, NttAvatarVerifiedIcon],
})
export class NttAvatar {
  readonly size = input<AvatarSize>('md');
  readonly src = input<string | null>(null);
  readonly alt = input<string>('');
  readonly initials = input<string | null>(null);
  readonly placeholder = input<boolean>(true);
  readonly online = input<AvatarOnlineStatus>(null);
  readonly verified = input<boolean>(false);
  readonly imageLoading = input<ImageLoading>('eager');
  readonly imageFetchPriority = input<ImageFetchPriority>('auto');

  private readonly _contentSlot = contentChild(NttAvatarContent);
  private readonly _companyIconSlot = contentChild(NttAvatarCompanyIcon);
  private readonly _failedImageSource = signal<string | null>(null);

  protected readonly classes = computed(() =>
    ['ntt-avatar', `ntt-avatar--${this.size()}`].join(' '),
  );
  protected readonly hasCustomContent = computed(() => !!this._contentSlot());
  protected readonly hasCompanyIcon = computed(() => !!this._companyIconSlot());

  protected readonly showImage = computed(() => {
    const source = this.src();
    return !!source && source !== this._failedImageSource();
  });
  protected readonly showInitials = computed(
    () => !this.showImage() && !!this.initials(),
  );
  protected readonly showPlaceholder = computed(
    () => !this.showImage() && !this.initials() && this.placeholder(),
  );
  protected readonly imageSize = computed(
    () => AVATAR_IMAGE_SIZES[this.size()],
  );

  protected readonly showOnlineIndicator = computed(
    () => this.online() !== null,
  );
  protected readonly isOnline = computed(() => this.online() === 'online');

  protected onImageError(): void {
    this._failedImageSource.set(this.src());
  }
}

const AVATAR_IMAGE_SIZES: Record<AvatarSize, number> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
  xl: 56,
  '2xl': 64,
};
